import React, { useRef, useEffect } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Offcanvas, Dropdown } from "bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { logout, fetchUserProfile } from "../store/slices/authSlice";
import { setSearchFilter } from "../store/slices/communitySlice";
import {
  fetchNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  addLiveNotification,
} from "../store/slices/notificationSlice";
import { showToast } from "../store/slices/uiSlice";
import socketService from "../services/socket";
import DummyUser from "../assets/images/avatar.png";

const MENU = [
  { label: "Home", path: "/" },
  { label: "Community", path: "/communities" },
  { label: "Teachers", path: "/teachers" },
  { label: "Courses", path: "/courses" },
  { label: "Classes", path: "/classes" },
  // { label: "Practice", path: "/practice" },
  { label: "Video Feed", path: "/video-feed" },
  { label: "About Us", path: "/about" },
  {
    label: "In App",
    icon: "bi-phone-fill",
    dropdownHeader: "Mobile App Features",
    children: [
      { label: "Feed", path: "/feed", icon: "bi-play-circle-fill", desc: "Explore videos & posts" },
      // { label: "Practice", path: "/practice", icon: "bi-mic-fill", desc: "Speaking exercises" },
      { label: "Friends", path: "/friends", icon: "bi-people-fill", desc: "Connect with learners" },
    ],
  },
];

export const Header = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const offcanvasRef = useRef(null);
  const { isAuthenticated, user, token } = useSelector((state) => state.auth);
  const searchFilter = useSelector((state) => state.community?.filters?.search || '');

  const { notifications, unreadCount, loading } = useSelector((state) => state.notifications || { notifications: [], unreadCount: 0, loading: false });

  useEffect(() => {
    if (isAuthenticated && !user && token) {
      dispatch(fetchUserProfile());
    }
  }, [isAuthenticated, user, token, dispatch]);

  // Fetch notifications
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchNotifications({ page: 0, limit: 15 }));
    }
  }, [isAuthenticated, dispatch]);

  // Live Socket Notifications Connection
  useEffect(() => {
    let socket;
    if (isAuthenticated && user?._id) {
      socket = socketService.connect();
      socketService.authenticate(user._id);

      const handleNotification = (data) => {
        console.log('Socket [notificationReceived] in Header:', data);
        if (data) {
          const formattedNotification = typeof data === 'string'
            ? { _id: Date.now().toString(), message: data, isRead: false, createdAt: new Date().toISOString() }
            : { _id: data._id || data.id || Date.now().toString(), isRead: false, createdAt: new Date().toISOString(), ...data };

          dispatch(addLiveNotification(formattedNotification));
          dispatch(showToast({
            message: formattedNotification.message || formattedNotification.title || 'New notification received',
            type: 'primary'
          }));
        }
      };

      socket.on('notificationReceived', handleNotification);

      return () => {
        if (socket) {
          socket.off('notificationReceived', handleNotification);
        }
      };
    }
  }, [isAuthenticated, user?._id, dispatch]);

  const getTimeAgo = (dateStr) => {
    if (!dateStr) return '';
    const now = new Date();
    const then = new Date(dateStr);
    const diffMs = now - then;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) {
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      if (diffHours === 0) {
        const diffMins = Math.floor(diffMs / (1000 * 60));
        return diffMins <= 1 ? 'just now' : `${diffMins}m ago`;
      }
      return `${diffHours}h ago`;
    }
    if (diffDays === 1) return 'yesterday';
    if (diffDays < 30) return `${diffDays}d ago`;
    const diffMonths = Math.floor(diffDays / 30);
    return `${diffMonths}mo ago`;
  };

  const getNavigationPath = (actionUrl, actionModel, actionId) => {
    if (!actionId) return actionUrl || '';

    const model = (actionModel || '').toLowerCase();
    
    if (model.includes('class')) {
      return `/class-detail/${actionId}`;
    }
    if (model.includes('course')) {
      return `/course-detail/${actionId}`;
    }
    if (model.includes('event')) {
      return `/event-detail/${actionId}`;
    }
    if (model.includes('lesson')) {
      return `/lesson-detail/${actionId}`;
    }
    if (model.includes('teacher')) {
      return `/teachers/${actionId}`;
    }
    if (model.includes('community')) {
      return `/community-detail/${actionId}`;
    }
    if (model.includes('post')) {
      return `/video-feed`;
    }

    if (actionUrl) {
      if (actionUrl.includes(actionId)) {
        return actionUrl;
      }
      
      const cleanUrl = actionUrl.trim();
      if (cleanUrl === '/posts' || cleanUrl.startsWith('/post')) {
        return `/video-feed`;
      }
      if (cleanUrl === '/class' || cleanUrl === '/classes') {
        return `/class-detail/${actionId}`;
      }
      if (cleanUrl === '/course' || cleanUrl === '/courses') {
        return `/course-detail/${actionId}`;
      }
      if (cleanUrl === '/community' || cleanUrl === '/communities') {
        return `/community-detail/${actionId}`;
      }
      if (cleanUrl === '/event' || cleanUrl === '/events') {
        return `/event-detail/${actionId}`;
      }
      if (cleanUrl === '/lesson' || cleanUrl === '/lessons') {
        return `/lesson-detail/${actionId}`;
      }
      if (cleanUrl.endsWith('/')) {
        return `${cleanUrl}${actionId}`;
      }
      return cleanUrl;
    }
    
    return '';
  };

  const handleNotificationClick = (item) => {
    const id = item._id || item.id;
    const isUnread = !(item.read || item.isRead);
    if (isUnread && id) {
      dispatch(markNotificationAsRead(id));
    }
    const actionUrl = item.action?.actionUrl || item.link || item.url;
    const actionModel = item.action?.actionModel || item.model;
    const actionId = item.action?.actionId || item.actionId;

    const navPath = getNavigationPath(actionUrl, actionModel, actionId);
    if (navPath) {
      navigate(navPath);
    }
  };

  const handleMarkAllAsRead = (e) => {
    e.stopPropagation();
    dispatch(markAllNotificationsAsRead());
  };

  // Handle navigation + close sidebar
  const handleNavigate = (path) => {
    navigate(path);

    if (offcanvasRef.current) {
      const instance =
        Offcanvas.getInstance(offcanvasRef.current) ||
        new Offcanvas(offcanvasRef.current);

      instance.hide();
    }
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  return (
    <div className="top-header sticky-top">
      <nav className="navbar navbar-light bg-white navbar-expand-lg shadow-sm">
        <div className="container-xl">
          {/* Mobile Menu */}
          <button className="navbar-toggler shadow-none" type="button" data-bs-toggle="offcanvas" data-bs-target="#offcanvasMenu" aria-controls="offcanvasNavbar" aria-label="Toggle navigation" >
            <i className="bi bi-list fs-3"></i>
          </button>

          {/* Logo */}
          <Link className="navbar-brand logo-wrapper text-center text-decoration-none mx-2 ms-lg-0 me-lg-3 p-0" to="/">
            <img src={require("../assets/images/logo.png")} alt="Logo" className="img-fluid" />
          </Link>

          {/* Offcanvas (Menu) */}
          <div ref={offcanvasRef} className="offcanvas-lg offcanvas-start flex-grow-1" tabIndex="-1" id="offcanvasMenu" aria-labelledby="offcanvasNavbarLabel">
            <div className="offcanvas-header bg-white border-bottom">
              <h5 className="offcanvas-title text-dark fw-bold">Menu</h5>

              {/* Close Button */}
              <button type="button" className="btn-close"
                onClick={() => {
                  const instance =
                    Offcanvas.getInstance(offcanvasRef.current) ||
                    new Offcanvas(offcanvasRef.current);

                  instance.hide();
                }}
              ></button>
            </div>

            <div className="offcanvas-body">
              <ul className="navbar-nav menu-link-nav d-flex flex-column flex-lg-row align-items-lg-center gap-0 gap-lg-1 mx-lg-auto">
                {MENU.map((item) =>
                  item.children ? (
                    <li className="nav-item dropdown" key={item.label}>
                      <button
                        className="nav-link dropdown-togglee border-0 bg-transparent d-flex align-items-center gap-1"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                      >
                        {item.icon && <i className={`bi ${item.icon} text-primary`}></i>}
                        {item.label}
                      </button>
                      <ul className="dropdown-menu dropdown-menu-end border rounded-3 shadow mt-2" style={{ minWidth: '220px' }}>
                        {item.dropdownHeader && (
                          <li>
                            <div className="dropdown-header text-xs fw-bold text-gray-500 text-uppercase px-3 mb-1">
                              {item.dropdownHeader}
                            </div>
                          </li>
                        )}
                        {item.children.map((child) => (
                          <li key={child.path}>
                            <Link
                              className="dropdown-item rounded-2 py-2 px-3 d-flex align-items-center gap-3"
                              to={child.path}
                              onClick={() => handleNavigate(child.path)}
                            >
                              <div className="d-flex align-items-center justify-content-center bg-primary bg-opacity-10 rounded-2" style={{ width: '32px', height: '32px' }}>
                                <i className={`bi ${child.icon} text-primary`}></i>
                              </div>
                              <div>
                                <div className="fw-semibold text-gray-900 text-sm">{child.label}</div>
                                <div className="text-xs text-gray-500">{child.desc}</div>
                              </div>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </li>
                  ) : (
                    <li className="nav-item" key={item.path}>
                      <NavLink
                        to={item.path}
                        className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                        onClick={() => handleNavigate(item.path)}
                      >
                        {item.label}
                      </NavLink>
                    </li>
                  )
                )}
              </ul>
            </div>
          </div>

          {/* User Auth Section */}
          <div className="ms-lg-3 d-flex align-items-center gap-3">
            {/* {location.pathname === '/communities' && (
              <div className="header-search-wrapper d-none d-lg-block">
                <div className="input-group rounded overflow-hidden border align-items-center" style={{ maxWidth: '240px', padding: "0.375rem 0.75rem" }}>
                  <span className="text-gray-500 me-2"><i className="bi bi-search"></i></span>
                  <input
                    type="text"
                    className="form-control bg-transparent border-0 p-0 shadow-none text-sm text-gray-800"
                    placeholder="Search communities..."
                    value={searchFilter}
                    onChange={(e) => dispatch(setSearchFilter(e.target.value))}
                    style={{ fontSize: '0.85rem' }}
                  />
                  {searchFilter && (
                    <button className="btn btn-link p-0 border-0 ms-2 text-gray-400 hover-text-gray-600 d-flex align-items-center" onClick={() => dispatch(setSearchFilter(''))}>
                      <i className="bi bi-x-circle-fill text-muted"></i>
                    </button>
                  )}
                </div>
              </div>
            )} */}

            {/* Notification Dropdown */}
            {isAuthenticated && (
              <div className="dropdown notification-dropdown">
                <div
                  className="notification-bell-wrapper position-relative d-flex align-items-center justify-content-center pointer dropdown-toggle-no-caret"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  style={{ width: '32px', height: '32px' }}
                >
                  <i className="bi bi-bell fs-5 text-gray-700"></i>
                  {unreadCount > 0 && (
                    <span 
                      className="position-absolute bg-danger border border-white text-white rounded-circle d-flex align-items-center justify-content-center fw-bold" 
                      style={{ 
                        top: '-4px', 
                        right: '-6px', 
                        fontSize: '0.625rem', 
                        width: '16px', 
                        height: '16px',
                        padding: '1px'
                      }}
                    >
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </div>

                <div className="dropdown-menu dropdown-menu-end notification-menu shadow-lg border-0 py-0" style={{ width: '320px', borderRadius: '12px' }}>
                  <div className="d-flex align-items-center justify-content-between p-3 border-bottom">
                    <h6 className="m-0 fw-bold text-dark">Notifications</h6>
                    {unreadCount > 0 && (
                      <button 
                        className="btn btn-link p-0 text-decoration-none text-xs fw-semibold text-primary" 
                        onClick={handleMarkAllAsRead}
                      >
                        Mark all as read
                      </button>
                    )}
                  </div>

                  <div className="notification-list" style={{ maxHeight: '320px', overflowY: 'auto' }}>
                    {loading ? (
                      <div className="text-center py-4">
                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    ) : notifications.length === 0 ? (
                      <div className="text-center py-4 text-muted">
                        <i className="bi bi-bell-slash fs-4 d-block mb-1 opacity-50"></i>
                        <span className="text-xs">No notifications yet</span>
                      </div>
                    ) : (
                      notifications.map((item) => {
                        const isUnread = !(item.read || item.isRead);
                        const nTitle = item.notification?.title || item.title || '';
                        const nMessage = item.notification?.message || item.message || item.content || '';
                        const nTime = item.receivedAt || item.createdAt || item.notification?.sentAt || item.timestamp;
                        const avatar = item.notification?.profile_picture || item.profile_picture || DummyUser;

                        return (
                          <div
                            key={item._id || item.id}
                            className={`notification-item d-flex align-items-start gap-2 p-3 border-bottom pointer ${isUnread ? 'bg-light-purple-10' : ''}`}
                            onClick={() => handleNotificationClick(item)}
                          >
                            <div className="notification-avatar rounded-circle overflow-hidden flex-shrink-0 border" style={{ width: '32px', height: '32px' }}>
                              <img src={avatar} className="w-100 h-100 object-fit-cover" alt="avatar" />
                            </div>
                            <div className="flex-grow-1">
                              {nTitle && (
                                <h6 className={`m-0 text-sm ${isUnread ? 'fw-bold text-dark' : 'fw-semibold text-gray-700'}`}>
                                  {nTitle}
                                </h6>
                              )}
                              <p className={`m-0 text-sm mt-1 ${isUnread ? 'fw-semibold text-dark' : 'text-gray-600'}`}>
                                {nMessage}
                              </p>
                              <span className="text-xs text-muted d-block mt-1">
                                {getTimeAgo(nTime)}
                              </span>
                            </div>
                            {isUnread && (
                              <span className="bg-primary rounded-circle mt-1" style={{ width: '6px', height: '6px', flexShrink: 0 }}></span>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                  <div className="p-2 border-top text-center bg-light">
                    <Link 
                      to="/notifications" 
                      className="text-xs fw-semibold text-primary text-decoration-none d-block py-1"
                    >
                      View All Notifications
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {isAuthenticated ? (
              <div className="user-profile-dropdown dropdown">
                <button className="dropdown-toggle-no-caret bg-transparent border-0 p-0" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                  <div className="user-avatar border">
                    <img src={user?.profile_picture || DummyUser} alt="User" className="w-100 h-100 object-fit-cover" />
                  </div>
                  {/* <div className="d-none d-md-block text-start">
                    <p className="m-0 text-sm fw-bold text-dark lh-1">{user?.full_name || 'User'}</p>
                    <small className="text-muted text-xs">{user?.role || 'Member'}</small>
                  </div> */}
                </button>
                <ul className="dropdown-menu dropdown-menu-end border rounded-3 shadow mt-2" style={{ minWidth: '220px' }}>
                  <li>
                    <div className="d-none d-md-block text-start px-3 py-2 border-bottom mb-2">
                      <p className="m-0 text-sm fw-bold text-dark lh-1">{user?.full_name || 'User'}</p>
                      <small className="text-muted text-xs">{user?.role || 'Member'}</small>
                    </div>
                  </li>
                  <li><Link className="dropdown-item" to="/profile"><i className="bi bi-person me-1"></i> Profile</Link></li>
                  {user?.user_type === 'teacher' && (
                    <li><Link className="dropdown-item" to="/revenue"><i className="bi bi-currency-dollar me-1"></i> Earnings</Link></li>
                  )}
                  <li><Link className="dropdown-item" to="/settings"><i className="bi bi-gear me-1"></i> Settings</Link></li>
                  <li><button className="dropdown-item text-danger" onClick={handleLogout}><i className="bi bi-box-arrow-right me-1"></i> Logout</button></li>
                </ul>
              </div>
            ) : (
              <div className="auth-btns d-flex align-items-center gap-3">
                <Link to="/login" className="text-decoration-none fw-bold text-dark d-none d-sm-block">Login</Link>
                <Link to="/signup" className="btn btn-primary rounded-pill shadow-sm">Join Free</Link>
              </div>
            )}
          </div>
        </div>
      </nav>
    </div>
  );
};
